import { Controller } from "stimulus";
import Quill from "quill";
import DOMPurify from "dompurify";

// clipboard.convert(string) parses into the live page before Quill filters formats; paste never passes a string.
const Clipboard = Quill.import("modules/clipboard");

// What the editor produces, and so what stored values hold: its formatting, colours, links, lists and
// alignment. RichTextSanitizer keeps the same on save. A link keeps its href only for http, https and
// mailto; target and rel are not URLs, so they skip that check.
const EDITOR_MARKUP = {
  ALLOWED_TAGS: ["p", "br", "strong", "b", "em", "i", "u", "span", "a", "ul", "ol", "li"],
  ALLOWED_ATTR: ["style", "class", "href", "target", "rel"],
  ALLOWED_URI_REGEXP: /^(?:https?|mailto):/i,
  ADD_URI_SAFE_ATTR: ["target", "rel"],
  ALLOW_ARIA_ATTR: false,
  ALLOW_DATA_ATTR: false
};

// The editor aligns with a class and colours with a style; no other class or style property is
// kept, as any other property could load a resource (background-image: url(...)) while the
// clipboard holds the markup in the page. The hook is on an instance of its own so it applies to
// no other DOMPurify caller.
const ALIGNMENT_CLASS = /^ql-align-(?:center|right|justify)$/;
const COLOUR_PROPERTIES = ["color", "background-color"];

// The browser parses the declarations, so a value that is not a valid colour is dropped. A
// detached element loads nothing.
const keepColours = (style) => {
  const parsed = document.createElement("span").style;
  parsed.cssText = style;

  return COLOUR_PROPERTIES.filter((property) => parsed.getPropertyValue(property))
    .map((property) => `${property}: ${parsed.getPropertyValue(property)};`)
    .join(" ");
};

const KEEP_ONLY = {
  class: (value) => value.split(/\s+/).filter((name) => ALIGNMENT_CLASS.test(name)).join(" "),
  style: keepColours
};

// eslint-disable-next-line new-cap -- DOMPurify(window) is how DOMPurify creates a separate instance
const purifier = DOMPurify(window);

purifier.addHook("uponSanitizeAttribute", (_node, data) => {
  const keepOnly = KEEP_ONLY[data.attrName];
  if (!keepOnly) {
    return;
  }

  data.attrValue = keepOnly(data.attrValue);
  data.keepAttr = data.attrValue.length > 0;
});

class SanitizingClipboard extends Clipboard {
  convert(html) {
    if (typeof html !== "string") {
      return super.convert(html);
    }

    return super.convert(purifier.sanitize(html, EDITOR_MARKUP));
  }
}

Quill.register("modules/clipboard", SanitizingClipboard, true);

export default class extends Controller {
  static get targets() {
    return ["hiddenField", "display"];
  }

  connect() {
    this.load();
  }

  load() {
    this.editor = new Quill("#" + this.displayTarget.id, this.options);
    this.editor.on("editor-change", this.updateHiddenField.bind(this));

    if (this.hiddenFieldTarget.value) {
      this.editor.setContents(this.editor.clipboard.convert(this.hiddenFieldTarget.value));
    }
  }

  updateHiddenField() {
    const html = this.editor.root.innerHTML;
    const parsedContent = new DOMParser().parseFromString(html, "text/html");
    this.hiddenFieldTarget.value = parsedContent.body.textContent ? html : "";
  }

  handleHitEnter(event) {
    const enterKeyCode = 13;

    if (event.keyCode === enterKeyCode) {
      event.stopPropagation();
    }
  }

  get options() {
    return {
      placeholder: this.displayTarget.dataset.placeholder,
      modules: {
        toolbar: this.displayTarget.dataset.tools.split(" ")
      },
      theme: this.displayTarget.dataset.theme
    };
  }
}
